import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { apiClient } from "@/lib/api";

const fmtNum = (n, digits = 4) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "-";
  return v.toFixed(digits);
};

const fmtPct = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "-";
  return `${v.toFixed(2)}%`;
};

const RiskBadge = ({ level }) => {
  const cls = level === "CRITICAL"
    ? "bg-red-700 text-white"
    : level === "ELEVATED"
      ? "bg-amber-600 text-white"
      : "bg-emerald-700 text-white";
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${cls}`} data-testid="user-risk-level-badge">
      {level || "NORMAL"}
    </span>
  );
};

const REGIME_STYLE = {
  TRENDING_UP: { cls: "bg-emerald-700 text-white", label: "📈 TRENDING UP" },
  TRENDING_DOWN: { cls: "bg-red-700 text-white", label: "📉 TRENDING DOWN" },
  RANGING: { cls: "bg-sky-700 text-white", label: "↔ RANGING" },
  HIGH_VOLATILITY: { cls: "bg-amber-600 text-black", label: "⚡ HIGH VOL" },
  LOW_VOLATILITY: { cls: "bg-slate-600 text-white", label: "🌙 LOW VOL" },
  UNKNOWN: { cls: "bg-slate-700 text-slate-300", label: "❔ UNKNOWN" },
  DISABLED: { cls: "bg-slate-800 text-slate-500", label: "○ DISABLED" },
};

const RegimeChip = ({ regime }) => {
  const r = regime || "UNKNOWN";
  const style = REGIME_STYLE[r] || REGIME_STYLE.UNKNOWN;
  return (
    <span
      className={`px-2 py-0.5 rounded text-xs font-semibold ${style.cls}`}
      data-testid="user-regime-chip"
      title={`Market regime: ${r}`}
    >
      {style.label}
    </span>
  );
};

const MetricCard = ({ label, value, testId, accent }) => (
  <article
    className="rounded border border-slate-700 bg-slate-900 p-3"
    data-testid={`${testId}-card`}
  >
    <p className="text-xs uppercase tracking-wider text-slate-400">{label}</p>
    <p
      className={`mt-1 text-lg font-bold ${accent || "text-emerald-300"}`}
      data-testid={`${testId}-value`}
    >
      {value ?? "-"}
    </p>
  </article>
);

export default function UserRiskMonitorPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [risk, setRisk] = useState(null);
  const [worstStrategies, setWorstStrategies] = useState([]);
  const [bots, setBots] = useState([]);
  const [selectedBotId, setSelectedBotId] = useState(null);
  const [sizingPreview, setSizingPreview] = useState(null);
  const [expandedSid, setExpandedSid] = useState(null);
  const [insights, setInsights] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [riskRes, worstRes, botsRes, insightsRes] = await Promise.all([
        apiClient.get("/user/futures/risk"),
        apiClient.get("/user/strategies/worst", { params: { limit: 5, lookback_days: 30 } }),
        apiClient.get("/bot-profiles").catch(() => ({ data: [] })),
        apiClient.get("/user/learning/insights").catch(() => ({ data: null })),
      ]);
      setRisk(riskRes.data || null);
      setWorstStrategies(worstRes.data?.worst_strategies || []);
      const botsList = Array.isArray(botsRes.data) ? botsRes.data : (botsRes.data?.items || []);
      setBots(botsList);
      if (botsList.length > 0 && !selectedBotId) setSelectedBotId(botsList[0].id);
      setInsights(insightsRes.data || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Risk monitor verisi alınamadı");
    } finally {
      setLoading(false);
    }
  }, [selectedBotId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const loadSizingPreview = useCallback(async (botId) => {
    if (!botId) return;
    try {
      const res = await apiClient.get(`/user/bots/${botId}/sizing-preview`);
      setSizingPreview(res.data || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bot sizing preview alınamadı");
    }
  }, []);

  useEffect(() => {
    if (selectedBotId) loadSizingPreview(selectedBotId);
  }, [selectedBotId, loadSizingPreview]);

  const positionsCount = useMemo(() => risk?.positions_count ?? 0, [risk]);
  const criticalCount = useMemo(() => risk?.critical_positions_count ?? 0, [risk]);

  return (
    <section
      className="space-y-6 bg-slate-950 p-6 text-slate-100"
      data-testid="user-risk-monitor-page"
    >
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" data-testid="user-risk-monitor-title">
            Risk Monitor & Bot Sizing
          </h1>
          <p className="text-sm text-slate-400">
            Sadece sizin pozisyonlarınız — admin görünümünden bağımsız, real-time risk özeti.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {insights?.regime?.detected_regime && (
            <RegimeChip regime={insights.regime.detected_regime} />
          )}
          <RiskBadge level={risk?.risk_level} />
          <Button
            variant="outline"
            size="sm"
            onClick={loadAll}
            disabled={loading}
            data-testid="user-risk-refresh-btn"
          >
            {loading ? "Yükleniyor..." : "Yenile"}
          </Button>
        </div>
      </header>

      {/* ─── Risk Snapshot ─── */}
      <div
        className="grid grid-cols-2 gap-3 md:grid-cols-4"
        data-testid="user-risk-snapshot-grid"
      >
        <MetricCard
          label="Açık Pozisyon"
          value={positionsCount}
          testId="user-risk-positions-count"
        />
        <MetricCard
          label="Kritik Pozisyon"
          value={criticalCount}
          testId="user-risk-critical-count"
          accent={criticalCount > 0 ? "text-red-400" : "text-emerald-300"}
        />
        <MetricCard
          label="Toplam Margin (USDT)"
          value={fmtNum(risk?.total_margin_used_usdt, 2)}
          testId="user-risk-total-margin"
        />
        <MetricCard
          label="Unrealized PnL (USDT)"
          value={fmtNum(risk?.total_unrealized_pnl_usdt, 2)}
          testId="user-risk-unrealized-pnl"
          accent={
            (risk?.total_unrealized_pnl_usdt ?? 0) < 0
              ? "text-red-400"
              : "text-emerald-300"
          }
        />
      </div>

      {/* ─── Positions Table ─── */}
      <section
        className="rounded border border-slate-700 bg-slate-900 p-4"
        data-testid="user-risk-positions-section"
      >
        <h2 className="mb-3 text-lg font-semibold">Açık Pozisyonlar</h2>
        {risk?.positions?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="user-risk-positions-table">
              <thead className="text-xs uppercase text-slate-400">
                <tr>
                  <th className="text-left py-2">Symbol</th>
                  <th className="text-left">Side</th>
                  <th className="text-right">Entry</th>
                  <th className="text-right">Mark</th>
                  <th className="text-right">Est. Liq</th>
                  <th className="text-right">Lev</th>
                  <th className="text-right">PnL</th>
                  <th className="text-right">Liq Dist %</th>
                </tr>
              </thead>
              <tbody>
                {risk.positions.map((p) => (
                  <tr
                    key={p.position_id}
                    className={`border-t border-slate-800 ${p.is_critical ? "bg-red-950/40" : ""}`}
                    data-testid={`user-risk-position-row-${p.position_id}`}
                  >
                    <td className="py-2">{p.symbol}</td>
                    <td>{p.side}</td>
                    <td className="text-right">{fmtNum(p.entry_price)}</td>
                    <td className="text-right">{fmtNum(p.current_price)}</td>
                    <td className="text-right">{fmtNum(p.estimated_liquidation_price)}</td>
                    <td className="text-right">{fmtNum(p.leverage, 1)}x</td>
                    <td
                      className={`text-right ${p.unrealized_pnl < 0 ? "text-red-400" : "text-emerald-300"}`}
                    >
                      {fmtNum(p.unrealized_pnl, 2)}
                    </td>
                    <td
                      className={`text-right ${p.is_critical ? "text-red-400 font-bold" : ""}`}
                    >
                      {fmtPct(p.liquidation_distance_pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-400" data-testid="user-risk-no-positions">
            Şu an açık pozisyonunuz yok.
          </p>
        )}
      </section>

      {/* ─── Bot Sizing Preview ─── */}
      <section
        className="rounded border border-slate-700 bg-slate-900 p-4"
        data-testid="user-bot-sizing-preview-section"
      >
        <h2 className="mb-3 text-lg font-semibold">Bot Sizing Preview</h2>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <label className="text-xs uppercase text-slate-400">Bot seç:</label>
          <select
            value={selectedBotId || ""}
            onChange={(e) => setSelectedBotId(e.target.value || null)}
            className="rounded border border-slate-700 bg-slate-800 px-2 py-1 text-sm"
            data-testid="user-bot-sizing-select"
            disabled={bots.length === 0}
          >
            <option value="">— bir bot seçin —</option>
            {bots.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} ({b.market_type || "?"})
              </option>
            ))}
          </select>
          <span className="text-xs text-slate-400" data-testid="user-bot-sizing-count">
            {bots.length} bot mevcut
          </span>
          {bots.length === 0 && !loading && (
            <button
              type="button"
              onClick={() => navigate("/user/bot-profiles")}
              className="rounded border border-emerald-600 bg-emerald-700/30 px-3 py-1 text-xs font-semibold text-emerald-200 hover:bg-emerald-700/50"
              data-testid="user-bot-sizing-empty-cta"
            >
              + Yeni Bot Oluştur
            </button>
          )}
        </div>
        {bots.length === 0 && !loading && (
          <p className="rounded border border-amber-700 bg-amber-900/30 p-3 text-sm text-amber-200" data-testid="user-no-bots-warning">
            ⚠ Henüz tanımlı botunuz yok. Risk önizlemesi yapmak için önce <strong>Bot Kur</strong> sayfasından bir bot oluşturun.
          </p>
        )}
        {sizingPreview ? (
          <div className="space-y-3">
            {/* 2026-07-08 Bulgu 3 — ZORUNLU banner: kapital kaynağı env fallback ise
                kullanıcı $1000 default'un gerçek sermayesi olmadığını her zaman görmeli */}
            {sizingPreview.capital_source && (
              <div
                className={`rounded border px-3 py-2 text-xs ${
                  sizingPreview.capital_source === "env_fallback"
                    ? "border-amber-800 bg-amber-950/40 text-amber-200"
                    : "border-sky-800 bg-sky-950/40 text-sky-200"
                }`}
                data-testid="bot-sizing-capital-source-banner"
                role="status"
              >
                <span className="font-semibold">
                  {sizingPreview.capital_source === "env_fallback"
                    ? "⚠ Risk Policy Yok: "
                    : "ℹ Kapital Bilgisi: "}
                </span>
                {sizingPreview.preview_warning}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <MetricCard
                label="Allocated (USDT)"
                value={fmtNum(sizingPreview.allocated_capital_usdt, 2)}
                testId="bot-sizing-allocated"
              />
              <MetricCard
                label="Leverage"
                value={`${fmtNum(sizingPreview.leverage, 1)}x`}
                testId="bot-sizing-leverage"
              />
              <MetricCard
                label="Risk / Trade"
                value={fmtPct(sizingPreview.risk_per_trade_pct)}
                testId="bot-sizing-risk-per-trade"
              />
              <MetricCard
                label="Size / Sinyal (USDT)"
                value={fmtNum(sizingPreview.estimated_size_per_signal_usdt, 2)}
                testId="bot-sizing-size-per-signal"
              />
              <MetricCard
                label="Max Exposure (USDT)"
                value={fmtNum(sizingPreview.estimated_max_exposure_usdt, 2)}
                testId="bot-sizing-max-exposure"
              />
              <MetricCard
                label="Max Liq Loss (USDT)"
                value={fmtNum(sizingPreview.estimated_max_liquidation_loss_usdt, 2)}
                testId="bot-sizing-max-liq-loss"
                accent="text-amber-300"
              />
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-400" data-testid="user-bot-sizing-empty">
            Bot seçildikten sonra preview burada görünür.
          </p>
        )}
        {sizingPreview?.preview_note && (
          <p className="mt-3 text-xs text-slate-500" data-testid="user-bot-sizing-note">
            ℹ {sizingPreview.preview_note}
          </p>
        )}
      </section>

      {/* ─── Learning Insights (L3 + L4 görselleştirme) ─── */}
      {insights && (
        <section
          className="rounded border border-slate-700 bg-slate-900 p-4"
          data-testid="user-learning-insights-section"
        >
          <h2 className="mb-3 text-lg font-semibold">
            Öğrenme & Piyasa Rejimi <span className="text-xs text-slate-500">(read-only öneri)</span>
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* L4 Regime Detail */}
            <article
              className="rounded border border-slate-800 bg-slate-950 p-3"
              data-testid="user-regime-card"
            >
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-slate-400">
                  Piyasa Rejimi (BTCUSDT 1h)
                </p>
                <RegimeChip regime={insights?.regime?.detected_regime} />
              </div>
              {insights?.regime?.enabled ? (
                <div className="space-y-1 text-xs text-slate-300">
                  <p>ATR %: <span className="font-mono">{fmtNum(insights.regime.atr_pct, 3)}</span></p>
                  <p>EMA20 / EMA50: <span className="font-mono">{fmtNum(insights.regime.ema20, 2)} / {fmtNum(insights.regime.ema50, 2)}</span></p>
                  <p>Close: <span className="font-mono">{fmtNum(insights.regime.last_close, 2)}</span></p>
                  <p>Bars: {insights.regime.bars_analyzed ?? "-"}</p>
                  {(insights.regime.strategy_bias_hint || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      <span className="text-slate-500 text-[10px] uppercase">Bias:</span>
                      {(insights.regime.strategy_bias_hint || []).map((h) => (
                        <span
                          key={h}
                          className="bg-slate-800 px-1.5 py-0.5 rounded text-[10px] font-mono text-slate-300"
                          data-testid={`regime-bias-${h}`}
                        >
                          {h}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-500">L4 disabled — admin tarafında etkinleştirilecek.</p>
              )}
            </article>

            {/* L3 Strategy Recommendations */}
            <article
              className="rounded border border-slate-800 bg-slate-950 p-3"
              data-testid="user-strategy-recommendations-card"
            >
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-slate-400">
                  Strateji Önerileri (L3)
                </p>
                <span className="text-[10px] text-slate-500" data-testid="user-strategy-recommendations-count">
                  {insights?.strategy_recommendations?.recommendations_count ?? 0} öneri
                </span>
              </div>
              {insights?.strategy_recommendations?.enabled ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {(insights.strategy_recommendations.recommendations || []).slice(0, 6).map((rec) => (
                    <div
                      key={rec.strategy_id}
                      className="border-t border-slate-800 pt-1.5"
                      data-testid={`user-recommendation-${rec.strategy_id}`}
                    >
                      <p className="font-mono text-xs font-semibold text-slate-200">{rec.strategy_id}</p>
                      <p className="text-[10px] text-slate-500">
                        Win %{((rec.win_rate || 0) * 100).toFixed(1)} · {rec.trade_count} trade
                      </p>
                      <div className="mt-0.5 flex flex-wrap gap-1">
                        {(rec.actions || []).map((a, i) => (
                          <span
                            key={i}
                            className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${
                              a.suggest?.startsWith("+")
                                ? "bg-emerald-900 text-emerald-200"
                                : a.suggest?.startsWith("-")
                                  ? "bg-red-900 text-red-200"
                                  : "bg-slate-800 text-slate-400"
                            }`}
                          >
                            {a.param}
                            {a.suggest ? ` ${a.suggest}` : ""}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  {(insights.strategy_recommendations.recommendations || []).length === 0 && (
                    <p className="text-xs text-slate-500">
                      Henüz yeterli işlem geçmişi yok. En az 10 trade gerekli.
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-500">L3 disabled.</p>
              )}
              <p className="mt-2 text-[10px] text-slate-600">
                ℹ Öneriler salt görseldir, sermayeyi otomatik değiştirmez.
              </p>
            </article>
          </div>
        </section>
      )}

      {/* ─── Worst Strategy Drilldown ─── */}
      <section
        className="rounded border border-slate-700 bg-slate-900 p-4"
        data-testid="user-worst-strategies-section"
      >
        <h2 className="mb-3 text-lg font-semibold">
          En Kötü Performans Gösteren Stratejiler (son 30 gün)
        </h2>
        {worstStrategies.length > 0 ? (
          <div className="space-y-2">
            {worstStrategies.map((s) => (
              <article
                key={s.strategy_id}
                className="rounded border border-slate-800 bg-slate-950 p-3"
                data-testid={`worst-strategy-${s.strategy_id}`}
              >
                <button
                  type="button"
                  className="flex w-full items-center justify-between text-left"
                  onClick={() => setExpandedSid(expandedSid === s.strategy_id ? null : s.strategy_id)}
                  data-testid={`worst-strategy-toggle-${s.strategy_id}`}
                >
                  <div>
                    <p className="font-mono text-sm font-semibold">{s.strategy_id}</p>
                    <p className="text-xs text-slate-400">
                      {s.trade_count} trade · Win %{(s.win_rate * 100).toFixed(1)} · PnL{" "}
                      <span className={s.total_pnl_usdt < 0 ? "text-red-400" : "text-emerald-300"}>
                        {fmtNum(s.total_pnl_usdt, 2)}
                      </span>
                    </p>
                  </div>
                  <span className="text-xs uppercase text-slate-500">
                    {s.recommendation}
                  </span>
                </button>
                {expandedSid === s.strategy_id && (
                  <div
                    className="mt-3 overflow-x-auto"
                    data-testid={`worst-strategy-recent-${s.strategy_id}`}
                  >
                    <table className="w-full text-xs">
                      <thead className="text-slate-400">
                        <tr>
                          <th className="text-left py-1">Symbol</th>
                          <th className="text-left">Side</th>
                          <th className="text-right">Entry</th>
                          <th className="text-right">Exit</th>
                          <th className="text-right">PnL</th>
                          <th className="text-right">Closed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(s.recent_trades || []).map((t, i) => (
                          <tr key={i} className="border-t border-slate-800">
                            <td className="py-1">{t.symbol}</td>
                            <td>{t.side}</td>
                            <td className="text-right">{fmtNum(t.entry_price)}</td>
                            <td className="text-right">{fmtNum(t.exit_price)}</td>
                            <td
                              className={`text-right ${t.pnl < 0 ? "text-red-400" : "text-emerald-300"}`}
                            >
                              {fmtNum(t.pnl, 2)}
                            </td>
                            <td className="text-right text-slate-500">
                              {t.closed_at
                                ? new Date(t.closed_at).toLocaleString("tr-TR")
                                : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </article>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400" data-testid="user-worst-strategies-empty">
            Son 30 günde yeterli işlem geçmişi yok (en az 3 kapanmış trade gerekli).
          </p>
        )}
      </section>
    </section>
  );
}
